import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Chip, MenuItem, Select, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import Highcharts from 'highcharts';
import { MAX_COMPARED_DEPLOYMENTS, SERIES_COLORS, SERIES_DASHES, UI } from './chartTheme';

export type ViewMode = 'single' | 'multi';

export interface DeploymentOption {
  deployment_id: string;
  deployment_name: string;
  site_name?: string;
}

export interface ComparedDeployment {
  id: string;
  name: string;
  siteName?: string;
  color: string;
  dash: Highcharts.DashStyleValue;
  isPrimary: boolean;
}

// ── Segmented toggle ────────────────────────────────────────────────────────

interface SegmentOption<T extends string> {
  value: T;
  label: string;
  disabledReason?: string;
}

interface SegmentedToggleProps<T extends string> {
  value: T;
  options: SegmentOption<T>[];
  onChange: (value: T) => void;
  ariaLabel: string;
  size?: 'small' | 'medium';
}

export function SegmentedToggle<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  size = 'medium',
}: SegmentedToggleProps<T>) {
  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      onChange={(_, next: T | null) => next && onChange(next)}
      aria-label={ariaLabel}
      sx={{
        bgcolor: 'rgba(255,255,255,0.03)',
        border: `1px solid ${UI.border}`,
        borderRadius: '8px',
        p: '3px',
        gap: '3px',
        '& .MuiToggleButton-root': {
          border: 'none',
          borderRadius: '6px !important',
          px: size === 'small' ? 1.25 : 1.75,
          py: size === 'small' ? 0.25 : 0.5,
          fontSize: size === 'small' ? '10.5px' : '11.5px',
          fontWeight: 700,
          textTransform: 'none',
          color: UI.textSecondary,
          lineHeight: 1.6,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', color: UI.text },
          '&.Mui-selected': {
            bgcolor: 'rgba(16,185,129,0.16)',
            color: UI.accent,
            '&:hover': { bgcolor: 'rgba(16,185,129,0.22)' },
          },
          '&.Mui-disabled': { color: '#3D3D3D', border: 'none' },
        },
      }}
    >
      {options.map((opt) => {
        const button = (
          <ToggleButton key={opt.value} value={opt.value} disabled={Boolean(opt.disabledReason)}>
            {opt.label}
          </ToggleButton>
        );
        // Disabled buttons don't fire mouse events, so the tooltip needs a wrapper
        return opt.disabledReason ? (
          <Tooltip key={opt.value} title={opt.disabledReason} arrow>
            <span>{button}</span>
          </Tooltip>
        ) : button;
      })}
    </ToggleButtonGroup>
  );
}

export const ViewModeToggle: React.FC<{
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
  multiDisabledReason?: string;
}> = ({ value, onChange, multiDisabledReason }) => (
  <SegmentedToggle<ViewMode>
    ariaLabel="Deployment view"
    value={value}
    onChange={onChange}
    options={[
      { value: 'single', label: 'Single Deployment' },
      { value: 'multi', label: 'Multi-Deployment', disabledReason: multiDisabledReason },
    ]}
  />
);

// ── Series key swatch ───────────────────────────────────────────────────────

const DASH_CSS: Partial<Record<Highcharts.DashStyleValue, string>> = {
  Solid: 'solid',
  ShortDash: 'dashed',
  LongDash: 'dashed',
  ShortDot: 'dotted',
  ShortDashDot: 'dashed',
};

export const SeriesSwatch: React.FC<{ color: string; dash?: Highcharts.DashStyleValue }> = ({ color, dash = 'Solid' }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      display: 'inline-block',
      width: 16,
      height: 0,
      borderTop: `2.5px ${DASH_CSS[dash] ?? 'solid'} ${color}`,
      flexShrink: 0,
    }}
  />
);

// ── Deployment selection ────────────────────────────────────────────────────

const freeSlot = (taken: { slot: number }[]): number => {
  for (let s = 0; s < MAX_COMPARED_DEPLOYMENTS; s++) {
    if (!taken.some((t) => t.slot === s)) return s;
  }
  return -1;
};

/**
 * Tracks which deployments are compared. Each keeps the colour slot it was given when
 * added, so removing one never repaints the others. The primary is always included.
 */
export const useComparedDeployments = (
  primaryId: string | undefined,
  deploymentsList: DeploymentOption[],
) => {
  const [slots, setSlots] = useState<{ id: string; slot: number }[]>([]);

  useEffect(() => {
    if (!primaryId) return;
    setSlots((prev) => {
      const known = prev.filter((p) => deploymentsList.some((d) => d.deployment_id === p.id));
      if (known.some((p) => p.id === primaryId)) return known;
      // New primary: keep up to MAX-1 of the existing picks, default to one comparison
      let next = known.slice(0, MAX_COMPARED_DEPLOYMENTS - 1);
      next = [{ id: primaryId, slot: freeSlot(next) }, ...next];
      if (next.length === 1) {
        const other = deploymentsList.find((d) => d.deployment_id !== primaryId);
        if (other) next.push({ id: other.deployment_id, slot: freeSlot(next) });
      }
      return next;
    });
  }, [primaryId, deploymentsList]);

  const compared: ComparedDeployment[] = useMemo(
    () => slots
      .map(({ id, slot }): ComparedDeployment | null => {
        const dep = deploymentsList.find((d) => d.deployment_id === id);
        return dep ? {
          id,
          name: dep.deployment_name,
          siteName: dep.site_name,
          color: SERIES_COLORS[slot],
          dash: SERIES_DASHES[slot],
          isPrimary: id === primaryId,
        } : null;
      })
      .filter((d): d is ComparedDeployment => d !== null)
      // Primary first, then in the order they were added
      .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary)),
    [slots, deploymentsList, primaryId],
  );

  const add = useCallback((id: string) => {
    setSlots((prev) => {
      if (prev.some((p) => p.id === id) || prev.length >= MAX_COMPARED_DEPLOYMENTS) return prev;
      return [...prev, { id, slot: freeSlot(prev) }];
    });
  }, []);

  const remove = useCallback((id: string) => {
    if (id === primaryId) return;
    setSlots((prev) => prev.filter((p) => p.id !== id));
  }, [primaryId]);

  return { compared, add, remove };
};

interface DeploymentPickerProps {
  compared: ComparedDeployment[];
  deploymentsList: DeploymentOption[];
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
}

export const DeploymentPicker: React.FC<DeploymentPickerProps> = ({ compared, deploymentsList, onAdd, onRemove }) => {
  const available = deploymentsList.filter((d) => !compared.some((c) => c.id === d.deployment_id));
  const atLimit = compared.length >= MAX_COMPARED_DEPLOYMENTS;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      <Typography sx={{ fontSize: '10px', color: UI.textMuted, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', mr: 0.5 }}>
        Deployments
      </Typography>
      {compared.map((d) => (
        <Chip
          key={d.id}
          size="small"
          icon={<Box sx={{ display: 'flex', alignItems: 'center', pl: 0.75 }}><SeriesSwatch color={d.color} dash={d.dash} /></Box>}
          label={
            <span>
              {d.name}
              {d.isPrimary && <span style={{ color: UI.textMuted, marginLeft: 6 }}>primary</span>}
            </span>
          }
          onDelete={d.isPrimary ? undefined : () => onRemove(d.id)}
          title={d.siteName ? `${d.name} · ${d.siteName}` : d.name}
          sx={{
            height: 26,
            bgcolor: 'rgba(255,255,255,0.03)',
            border: `1px solid ${UI.border}`,
            color: UI.text,
            fontSize: '11px',
            fontWeight: 600,
            '& .MuiChip-deleteIcon': { color: UI.textMuted, fontSize: 16, '&:hover': { color: '#E66767' } },
          }}
        />
      ))}
      {available.length > 0 && (
        <Tooltip title={atLimit ? `Compare up to ${MAX_COMPARED_DEPLOYMENTS} deployments at once` : ''} arrow>
          <span>
            <Select
              size="small"
              value=""
              displayEmpty
              disabled={atLimit}
              onChange={(e) => e.target.value && onAdd(String(e.target.value))}
              renderValue={() => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AddIcon sx={{ fontSize: 14 }} /> Add deployment
                </Box>
              )}
              inputProps={{ 'aria-label': 'Add deployment to comparison' }}
              sx={{
                height: 26,
                fontSize: '11px',
                fontWeight: 600,
                color: UI.accent,
                borderRadius: '13px',
                border: '1px dashed rgba(16,185,129,0.45)',
                '& .MuiSelect-select': { py: 0, pl: 1.25, pr: '28px !important', display: 'flex', alignItems: 'center' },
                '& .MuiSvgIcon-root': { color: UI.accent },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&.Mui-disabled': { borderColor: '#2A2A2A', '& .MuiSelect-select': { color: '#4A4A4A', WebkitTextFillColor: '#4A4A4A' } },
              }}
              MenuProps={{ PaperProps: { sx: { bgcolor: '#111', border: '1px solid #222', color: '#fff', maxHeight: 300 } } }}
            >
              {available.map((d) => (
                <MenuItem key={d.deployment_id} value={d.deployment_id} sx={{ fontSize: '12px' }}>
                  {d.deployment_name}
                  {d.site_name && <span style={{ color: UI.textMuted, marginLeft: 6 }}>{d.site_name}</span>}
                </MenuItem>
              ))}
            </Select>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};
